import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { FI_ORANGE } from '../theme/tokens';

export const PREVIEW_EXPANDED_WIDTH = 420;
export const PREVIEW_THUMB_WIDTH = 72;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

/** Stable PDF stage size — must NOT follow pane width (that causes FitH reflow / zoom jump on drag). */
const DOC_W = 720;
const DOC_H = 960;

/**
 * Hide Chromium/Firefox built-in PDF chrome so our −/%/+ is the only zoom UI.
 * No view=FitH / page-width — those re-layout when the iframe box changes.
 */
function pdfPreviewSrc(url: string): string {
  const base = url.split('#')[0];
  return `${base}#toolbar=0&navpanes=0&scrollbar=0&zoom=100`;
}

export interface DocumentPreviewRailProps {
  collapsed: boolean;
  fileName: string;
  fileUrl: string | null;
  fileType?: string;
  demo?: boolean;
  expandedWidth?: number;
  thumbWidth?: number;
  minHeight?: number;
  /** Show + / − zoom when expanded (PO outer-frame preview). */
  showZoom?: boolean;
  /** True while the host is drag-resizing the pane — freezes iframe hit-testing. */
  isResizing?: boolean;
  /** Clicking the thumbnail expands the rail (FieldInsight parity). */
  onExpand?: () => void;
  /** Optional: renders a × in the expanded header to clear the document. */
  onRemove?: () => void;
}

/**
 * Left-rail document preview for import / reconcile dialogs.
 * Expanded = full PDF/image pane; collapsed = narrow thumbnail strip (not hidden).
 * Pane width is a viewport only — document canvas size is fixed; zoom is CSS scale.
 */
export const DocumentPreviewRail: React.FC<DocumentPreviewRailProps> = ({
  collapsed,
  fileName,
  fileUrl,
  fileType,
  demo = false,
  expandedWidth = PREVIEW_EXPANDED_WIDTH,
  thumbWidth = PREVIEW_THUMB_WIDTH,
  minHeight = 320,
  showZoom = false,
  isResizing = false,
  onExpand,
  onRemove,
}) => {
  const [zoom, setZoom] = useState(1);
  useEffect(() => { setZoom(1); }, [fileUrl]);

  const width = collapsed ? thumbWidth : expandedWidth;
  const isPdf = fileType === 'application/pdf';
  const isImage = fileType?.startsWith('image/');

  const zoomLabel = `${Math.round(zoom * 100)}%`;
  const zoomIn = () => setZoom(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom(z => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));

  const previewBody = fileUrl && isPdf ? (
    <Box sx={{
      width: '100%', height: '100%', overflow: 'auto',
      backgroundColor: '#eceff1',
    }}>
      <Box sx={{ width: DOC_W * zoom, height: DOC_H * zoom, position: 'relative' }}>
        <iframe
          src={pdfPreviewSrc(fileUrl)}
          title="Document preview"
          style={{
            width: DOC_W,
            height: DOC_H,
            border: 'none',
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            backgroundColor: '#fff',
            pointerEvents: isResizing ? 'none' : 'auto',
          }}
        />
      </Box>
    </Box>
  ) : fileUrl && isImage ? (
    <Box sx={{
      width: '100%', height: '100%', overflow: 'auto',
      backgroundColor: '#eceff1', p: 1,
    }}>
      <img
        src={fileUrl}
        alt="Document"
        style={{
          width: DOC_W * zoom,
          height: 'auto',
          display: 'block',
          objectFit: 'contain',
          pointerEvents: isResizing ? 'none' : 'auto',
        }}
      />
    </Box>
  ) : (
    <Typography sx={{ fontSize: '0.68rem', color: '#aaa', p: 2, textAlign: 'center' }}>
      {demo ? 'Demo mode' : 'No preview'}
    </Typography>
  );

  return (
    <Box sx={{
      width,
      flexShrink: 0,
      overflow: 'hidden',
      // Animate only collapse ↔ expand — never while drag-resizing (causes jump).
      transition: collapsed ? 'width 0.22s ease' : 'none',
    }}>
      <Box sx={{
        height: '100%',
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid #eee',
        backgroundColor: collapsed ? '#f8f5ff' : '#fff',
      }}>
        {collapsed ? (
          <Box
            role="button"
            tabIndex={0}
            onClick={onExpand}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onExpand?.(); }}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: onExpand ? 'pointer' : 'default',
              overflow: 'hidden',
              '&:hover': onExpand ? { backgroundColor: '#f0ebfa' } : undefined,
            }}
          >
            <Box sx={{ py: 0.75, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              <PictureAsPdfIcon sx={{ fontSize: 16, color: FI_ORANGE }} />
              <Typography sx={{
                fontSize: '0.52rem', fontWeight: 700, color: '#6F6489',
                writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                maxHeight: 56, overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {fileName}
              </Typography>
            </Box>
            <Box sx={{
              flex: 1,
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
              borderTop: '1px solid #e8e0f5',
              backgroundColor: '#f5f5f5',
            }}>
              {fileUrl && isPdf ? (
                <iframe
                  src={pdfPreviewSrc(fileUrl)}
                  title="Document thumbnail"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: DOC_W,
                    height: DOC_H,
                    transform: `scale(${thumbWidth / DOC_W})`,
                    transformOrigin: 'top left',
                    border: 'none',
                    pointerEvents: 'none',
                  }}
                />
              ) : fileUrl && isImage ? (
                <img
                  src={fileUrl}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                />
              ) : (
                <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                  <PictureAsPdfIcon sx={{ fontSize: 22, color: '#c4b5d8' }} />
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{
              px: 1, py: 0.5, borderBottom: '1px solid #eee',
              display: 'flex', alignItems: 'center', gap: 0.35, backgroundColor: '#fafafa',
            }}>
              <PictureAsPdfIcon sx={{ fontSize: 13, color: FI_ORANGE }} />
              <Typography sx={{
                fontSize: '0.65rem', fontWeight: 700, color: '#444', flex: 1,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {fileName}
              </Typography>
              {showZoom && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.15, mr: 0.25 }}>
                  <Tooltip title="Zoom out" arrow>
                    <span>
                      <IconButton size="small" aria-label="Zoom out"
                        disabled={zoom <= ZOOM_MIN}
                        onClick={zoomOut} sx={{ p: 0.2 }}>
                        <RemoveIcon sx={{ fontSize: 14, color: '#666' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#888', minWidth: 32, textAlign: 'center' }}>
                    {zoomLabel}
                  </Typography>
                  <Tooltip title="Zoom in" arrow>
                    <span>
                      <IconButton size="small" aria-label="Zoom in"
                        disabled={zoom >= ZOOM_MAX}
                        onClick={zoomIn} sx={{ p: 0.2 }}>
                        <AddIcon sx={{ fontSize: 14, color: '#666' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
              {onRemove && (
                <Tooltip title="Remove document" arrow>
                  <IconButton size="small" aria-label="Remove document preview"
                    onClick={onRemove} sx={{ p: 0.25, ml: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14, color: '#999' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{
              flex: 1, overflow: 'hidden', backgroundColor: '#f5f5f5',
              minHeight: minHeight - 36,
            }}>
              {previewBody}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
